import { useFloatingPanel } from '@/components/admin/protocoles/step/floating-panel';
import { VERSION_TAG_LABEL, type VersionedWording } from '@/lib/versioned-labels';
import type { VersionSummary, VersionTag } from '@matter_extractor/shared';
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useState } from 'react';

/**
 * « Dupliquer » asks ONE question, and only when there is one (refonte
 * modulaire §4.2, issue #116).
 *
 * The gesture lives on the library card - the head of the editor offered it
 * too until #123 took it out - and §4.2 gives it one shape: a two-line menu,
 * « depuis production v3 » or « depuis développement v5 », **only when the two
 * pointers differ**; otherwise the click acts straight away. The rule stays
 * here, apart from the card that renders it: it is a pure decision, asserted on
 * its own, and the one thing a second surface would have to call rather than
 * re-decide - two copies of « when do we ask » is the pair that ends up
 * disagreeing on a card that was never passed to production.
 *
 * ## Never a dialog
 *
 * §4 refuses the creation box - « il n'y a pas de boîte de création » - and a
 * duplication that opened one would put a modal in front of the lightest
 * gesture of the screen. A menu is what the application already speaks here:
 * the version a protocol plays is chosen in one (§7.2), and the package `ActionMenu`
 * is the « … » of a card. This panel is the same surface with `role="menu"`,
 * because what one does in it is pick a line and leave - not read, and not
 * configure.
 *
 * ## The draft warning, and why it can OPEN the menu
 *
 * §4.2 asks the screen to say, before duplicating, that a draft newer than the
 * development version will not be copied. A tooltip says it to a mouse that
 * happens to rest; a footer under the two lines says it to everybody who is
 * about to choose. So a pending draft counts as something to ask about: the
 * menu opens for it even when one pointer would have acted directly, and the
 * single line it then carries is the answer to « from what, then ». That is one
 * extra click on an entity somebody is in the middle of editing, which is
 * exactly the case §4.2 wants slowed down.
 *
 * ## No `VersionPill` inside the lines, deliberately
 *
 * The pill carries a `data-testid` the CARD owns - `critere-pastille-production`
 * - and §4 makes its ABSENCE readable: that mark is how a screen and a test say
 * « never passed to production ». A second one inside a menu would make the mark
 * ambiguous on the very screen built around its absence. The lines say the same
 * thing in words.
 */

/** What the gesture needs to know about the entity it is offered on. */
export interface DuplicateSource {
  /** Null when nothing was ever passed: there is then only one line to offer. */
  readonly production: VersionSummary | null;
  readonly development: VersionSummary;
  /** A server draft that differs from the development version - the card's `hasPendingDraft`. */
  readonly hasPendingDraft: boolean;
}

/** One line of the menu: a tag, and the number its pointer sits on. */
export interface DuplicateTarget {
  readonly tag: VersionTag;
  readonly number: number;
}

/**
 * The lines to offer: two when the pointers sit on DIFFERENT versions, one
 * otherwise.
 *
 * Compared by version id and not by number, because that is the question §4.2
 * asks: two pointers on one version offer one content, so asking which to copy
 * would be a choice without a difference. When there is one line it is
 * `development`, which is the pointer §4 guarantees is never empty.
 */
export function duplicateTargets(source: DuplicateSource): readonly DuplicateTarget[] {
  const development: DuplicateTarget = { tag: 'development', number: source.development.number };
  const production = source.production;
  if (production === null || production.id === source.development.id) return [development];
  return [{ tag: 'production', number: production.number }, development];
}

/**
 * Whether the gesture has anything to put in front of the operator.
 *
 * Two things can: a real choice, and a draft that is about to be left behind
 * (§4.2). False means the click writes the copy on the spot.
 */
export function duplicateAsks(source: DuplicateSource): boolean {
  return duplicateTargets(source).length > 1 || source.hasPendingDraft;
}

/** The tag a direct click copies from - the only line there was to offer. */
export function directDuplicateTag(source: DuplicateSource): VersionTag {
  return duplicateTargets(source)[0]?.tag ?? 'development';
}

/** The width the placement needs before the panel has been laid out. */
const PANEL_WIDTH = 280;

export interface DuplicateChoiceProps {
  wording: VersionedWording;
  /** The entity's current name, for the menu's accessible name. */
  name: string;
  source: DuplicateSource;
  /** The control the panel stands against, and the focus goes back to. */
  anchor: HTMLElement | null;
  onPick: (tag: VersionTag) => void;
  onClose: () => void;
}

export function DuplicateChoice({
  wording,
  name,
  source,
  anchor,
  onPick,
  onClose,
}: DuplicateChoiceProps) {
  /*
    The placement, the Escape and the click outside come from the hook the
    package already has, and are not written a second time here: its own header
    states the lesson - four copies of these listeners existed, a defect was
    found on a capture, and it was fixed in one of them.
  */
  const { panelRef, at } = useFloatingPanel({ anchor, width: PANEL_WIDTH, onClose });
  const [panel, setPanel] = useState<HTMLElement | null>(null);
  const holdPanel = useCallback(
    (node: HTMLElement | null) => {
      setPanel(node);
      panelRef(node);
    },
    [panelRef],
  );

  /**
   * The first line takes the focus, as a menu does for the same role.
   *
   * On the next task and not in the effect itself: this panel opens from an entry of the
   * package `ActionMenu` (Radix), which hands the focus back to its trigger on a timer once
   * it closes - in the same commit this panel mounts in. Taken synchronously, the focus
   * would be pulled back to « … » right after.
   */
  useEffect(() => {
    const first = panel?.querySelector('[role="menuitem"]');
    if (!(first instanceof HTMLElement)) return;
    const timer = window.setTimeout(() => first.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [panel]);

  /**
   * The four keys `role="menu"` promises - a role without them is
   * the one combination that MISLEADS.
   *
   * Home and End are here and not only the two arrows: this panel tells its
   * reader it is « the same surface » as the package `ActionMenu`, and a menu that
   * answers two of its four keys is a promise kept by half.
   */
  function moveFocus(event: ReactKeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const items = [...(panel?.querySelectorAll('[role="menuitem"]') ?? [])].filter(
      (node): node is HTMLButtonElement => node instanceof HTMLButtonElement && !node.disabled,
    );
    if (items.length === 0) return;
    event.preventDefault();
    const here = items.findIndex((node) => node === document.activeElement);
    const last = items.length - 1;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowDown'
            ? here >= last
              ? 0
              : here + 1
            : here <= 0
              ? last
              : here - 1;
    items[next]?.focus();
  }

  return (
    <div
      ref={holdPanel}
      data-testid={`${wording.entity}-dupliquer-menu`}
      onKeyDown={moveFocus}
      style={{ left: at.left, top: at.top, width: PANEL_WIDTH }}
      className="fixed z-30 max-w-[calc(100vw-16px)] rounded-md border border-gray-200 bg-white py-1 text-left shadow-lg"
    >
      <div role="menu" aria-label={`Dupliquer « ${name} »`}>
        {duplicateTargets(source).map((target) => (
          <button
            key={target.tag}
            type="button"
            role="menuitem"
            data-testid={`${wording.entity}-dupliquer-${target.tag}`}
            onClick={() => {
              anchor?.focus();
              onClose();
              onPick(target.tag);
            }}
            className="block w-full cursor-pointer px-3 py-2 text-left text-gray-800 text-small hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            depuis {VERSION_TAG_LABEL[target.tag]} v{target.number}
          </button>
        ))}
      </div>
      {source.hasPendingDraft ? (
        <p
          data-testid={`${wording.entity}-dupliquer-brouillon`}
          className="mt-1 border-gray-200 border-t px-3 pt-2 pb-1 text-caption text-gray-600 leading-4"
        >
          Le brouillon en cours n’est pas copié : une copie part toujours d’une version enregistrée.
        </p>
      ) : null}
    </div>
  );
}
